import { DocumentStatus } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PoolSplitButton, PoolTooltip } from '@synergy/components-next';
import { useMemo } from 'react';
import { usePaloShortcut } from '@synergy/components-palo';
import { usePaloThemeGuard } from '@synergy/theme-palo';

import { FinancialCalculations } from '../../../../common/utils/financial-calculations';
import { InvoiceSteps } from '../models/invoice-steps';
import { PaloDocumentPhase, PaloFlowAction, usePaloFlowButtonProps } from '../../../detail-header/palo-main-action';
import { getActionStep } from '../utils/get-action-step';
import { invoiceDetailOverviewDataSelector } from '../store/overview/invoice-detail-overview-selectors';
import {
    invoiceDetailPositionsDataSelector,
    isCreditSelector,
} from '../store/positions/invoice-detail-positions-selectors';
import { invoiceDetailStepperStateSelector } from '../store/invoice-detail-selectors';
import { paloMissingFieldsMessage } from '../../../../common/components/palo/palo-missing-fields-message';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useInvoiceDetailContext } from '../../context/invoice-detail-context';

export interface PaloInvoiceDetailActionsProps {
    activeStep: InvoiceSteps;
    phase: PaloDocumentPhase;
    onPdfOpen: () => void;
    onComplete: (changeInvoiceNumberSequence: boolean) => void;
}

/**
 * What the invoice offers besides saving: its own next step.
 *
 * Printing moved into the three-dot menu with everything else that is not the
 * next step of the document (#2206).
 *
 * The buttons no longer change with the tab. "Speichern & Weiter" is gone with
 * the wizard, and finishing the invoice is offered wherever one stands in it —
 * the bar that changed under the cursor was the complaint (#2207).
 */
export const PaloInvoiceDetailActions = (props: PaloInvoiceDetailActionsProps): JSX.Element => {
    const { activeStep, phase, onPdfOpen, onComplete } = props;
    usePaloThemeGuard('PaloInvoiceDetailActions');
    const stepState = useAppSelector(invoiceDetailStepperStateSelector);
    const invoice = useAppSelector(invoiceDetailOverviewDataSelector);
    const positions = useAppSelector(invoiceDetailPositionsDataSelector);
    const { invoiceItems, netShippingCosts, netSurcharge } = useInvoiceDetailContext();
    const calculations = useMemo(
        () => FinancialCalculations.calculate(invoiceItems, netShippingCosts, netSurcharge),
        [invoiceItems, netShippingCosts, netSurcharge]
    );

    const isCredit = useAppSelector(isCreditSelector);
    const actionStep = getActionStep(invoice.values.status, isCredit);

    // What keeps the invoice from being closed, by name. "Check the form" left
    // the reader to find it, and the step holding it need not be the one open
    // (#2210, R-98).
    const missing = paloMissingFieldsMessage([
        { tab: 'app.invoices.detail.step.overview', errors: invoice.errors },
        { tab: 'app.invoices.detail.step.positions', errors: positions.errors },
    ]);
    // Completing the invoice is what moves it on, so it is the filled action
    // once there is nothing left to save — beside a white "Speichern", not a
    // second blue one (FRM-18, R-06).
    const completeAppearance = usePaloFlowButtonProps(phase, 'advance');
    // An invoice without a single position has nothing to bill, and closing
    // it gives it a number out of the sequence for nothing. The order has
    // refused that all along; the invoice let it through (LZ-05).
    const withoutPositions = 0 === invoiceItems.length;
    const disabled =
        withoutPositions ||
        (isCredit && calculations.grossTotal > 0) ||
        (activeStep === InvoiceSteps.Positions &&
            (stepState.errorSteps.length > 0 || invoice.values.status === DocumentStatus.Canceled));

    // F and R carry the same blockers as the button, but not its step: the
    // button only exists on the positions step, and a shortcut whose point is
    // to save the walk there must not demand the walk first. onComplete saves
    // the whole document before it asks, so it works from either step.
    const flowReady =
        !withoutPositions &&
        !(isCredit && calculations.grossTotal > 0) &&
        0 === stepState.errorSteps.length &&
        invoice.values.status !== DocumentStatus.Canceled;
    const completes = invoice.values.status !== DocumentStatus.Completed;

    usePaloShortcut({
        combo: 'f',
        group: 'Beleg',
        label: 'Abschließen',
        disabled: !flowReady || !completes,
        handler: () => onComplete(false),
    });
    usePaloShortcut({
        combo: 'r',
        group: 'Beleg',
        label: 'Stornieren',
        disabled: !flowReady || completes,
        handler: () => onComplete(false),
    });

    return (
        <>
            {DocumentStatus.Created === invoice.values.status ? (
                <PoolTooltip
                    title={
                        !disabled ? (
                            ''
                        ) : withoutPositions ? (
                            <FormattedMessage id="app.invoices.detail.completeNeedsPosition" />
                        ) : missing ? (
                            <FormattedMessage id="app.snackbar.formErrorsNamed" values={{ fields: missing }} />
                        ) : (
                            <FormattedMessage id="app.invoices.detail.creditNegative" />
                        )
                    }
                >
                    <PoolSplitButton
                        {...completeAppearance}
                        disabled={disabled}
                        menuItems={[
                            {
                                label: actionStep.label,
                                displayLabel: actionStep.label,
                                onClick: () => onComplete(false),
                            },
                            {
                                label: <FormattedMessage id="app.invoices.detail.invoiceNumberSequence.title" />,
                                displayLabel: (
                                    <FormattedMessage id="app.invoices.detail.invoiceNumberSequence.titleShort" />
                                ),
                                onClick: () => onComplete(true),
                            },
                        ]}
                        sx={{ flexShrink: 0 }}
                    />
                </PoolTooltip>
            ) : (
                /* Cancelling is not a step forward, so it stays white whatever
                   the state of the invoice is (FRM-18). */
                <PaloFlowAction phase={phase} kind="revoke" onClick={() => onComplete(false)} disabled={disabled}>
                    {actionStep.label}
                </PaloFlowAction>
            )}
        </>
    );
};
